// REMUS owns Keycloak initialisation directly instead of delegating to
// the previous shared KeycloakProvider, because it called keycloak.init() with
// generic defaults — which forces a token refresh roundtrip even when we
// hand it pre-issued bypass tokens. In mock-auth mode we want to skip
// keycloak.init() entirely; the structure mirrors hse-skeleton-app's
// KeycloakProvider but uses REMUS's LoadingComponent and exports.
import {ReactNode, useEffect, useRef, useState} from 'react';
import keycloak from '../../keycloak';
import LoadingComponent from '../../LoadingComponent';
import {getMockAuthTokens, isAppSsoMode, isLocalAuthBypassEnabled} from '../../config/mockAuth';

export type KeycloakProviderProps = {
    children: ReactNode;
};

type AuthState = 'initialising' | 'authenticated' | 'error';

function KeycloakProvider({children}: KeycloakProviderProps) {
    const [authState, setAuthState] = useState<AuthState>('initialising');
    const [errorMessage, setErrorMessage] = useState('');
    const isInitialized = useRef(false);

    useEffect(() => {
        if (isInitialized.current) {
            return;
        }
        isInitialized.current = true;

        const mockAuthTokens = getMockAuthTokens();

        // App SSO build: authenticate the OS-logged-in CERN user via Kerberos.
        // kc_idp_hint=kerberos jumps straight to CERN's Kerberos identity provider,
        // which issues the SPNEGO Negotiate challenge Chromium answers silently with
        // the Windows TGT (see electron/main.js's auth-server-allowlist). Without the
        // hint, the cern realm shows the username/password form instead — it does NOT
        // do Negotiate on its default login page. Re-used on token-refresh failure too.
        const loginOptions = isAppSsoMode()
            ? {scope: 'openid', idpHint: 'kerberos'}
            : {scope: 'openid'};

        // Locally-generated bypass tokens never hit auth.cern.ch — skip
        // keycloak.init() entirely and let the api/agent.ts interceptor
        // swap Authorization for ADFS_LOGIN + DEBUG_ROLES at request time.
        if (mockAuthTokens && isLocalAuthBypassEnabled()) {
            (window as any).keycloak = keycloak;
            setAuthState('authenticated');
            return;
        }

        if (mockAuthTokens) {
            // Playwright-injected tokens: keycloak-js only sets timeSkew
            // after a real token exchange. Pre-issued tokens skip that,
            // so pin it to avoid an immediate refresh loop.
            (keycloak as unknown as {timeSkew: number}).timeSkew = 0;
        } else {
            keycloak.onTokenExpired = () => {
                keycloak.updateToken(30).catch(() => {
                    keycloak.login(loginOptions);
                });
            };
        }

        const initOptions = mockAuthTokens
            ? {
                token: mockAuthTokens.token,
                refreshToken: mockAuthTokens.refreshToken,
                idToken: mockAuthTokens.idToken,
                checkLoginIframe: false,
            }
            : isAppSsoMode()
                ? {
                    // No onLoad: stay anonymous and drive login() ourselves so the
                    // same loginOptions are reused on token-refresh failure.
                    checkLoginIframe: false,
                }
                : {
                    onLoad: 'login-required' as const,
                    checkLoginIframe: false,
                };

        keycloak
            .init(initOptions as any)
            .then((authenticated: boolean) => {
                if (authenticated) {
                    (window as any).keycloak = keycloak;
                    setAuthState('authenticated');
                } else {
                    keycloak.login(loginOptions);
                }
            })
            .catch((e: Error) => {
                setAuthState('error');
                setErrorMessage(e?.message || 'Failed to initialise authentication');
            });

        return () => {
            keycloak.onTokenExpired = undefined;
        };
    }, []);

    if (authState === 'authenticated') {
        return <>{children}</>;
    }
    if (authState === 'error') {
        // Keep it minimal — same backdrop, just sit there. Users can refresh.
        // (REMUS has no UnauthorizedPage equivalent today.)
        // eslint-disable-next-line no-console
        console.error('KeycloakProvider: authentication failed —', errorMessage);
    }
    return <LoadingComponent/>;
}

export default KeycloakProvider;
